import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { auth, db } from '../Configuration';

// Create the context
const FirebaseDataContext = createContext(null);

// Custom hook to use the Firebase data context
export const useFirebaseData = () => {
  const context = useContext(FirebaseDataContext);
  if (!context) {
    throw new Error('useFirebaseData must be used within a FirebaseDataProvider');
  }
  return context;
};

// Provider component
export const FirebaseDataProvider = ({ children }) => {
  // Core data states
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [campuses, setCampuses] = useState([]);
  /** Full campus list before allowedCampus filter (for admin consolidated report). */
  const [campusesAllUnfiltered, setCampusesAllUnfiltered] = useState([]);
  const [tests, setTests] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [users, setUsers] = useState([]);
  const [studentMarks, setStudentMarks] = useState([]);
  const [instructors, setInstructors] = useState([]);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [loadingStates, setLoadingStates] = useState({
    students: false,
    courses: false,
    campuses: false,
    tests: false,
    attendance: false,
    users: false,
    studentMarks: false,
    instructors: false
  });

  // Error state
  const [error, setError] = useState(null);

  // Get allowed campuses from localStorage
  const getAllowedCampuses = useCallback(() => {
    try {
      const campusCode = JSON.parse(localStorage.getItem('campusCode')) || [];
      return Array.isArray(campusCode) ? campusCode.filter(Boolean) : [campusCode].filter(Boolean);
    } catch {
      return [];
    }
  }, []);

  const refreshCurrentUserAccess = useCallback(async () => {
    const email = auth.currentUser?.email?.toLowerCase();
    if (!email) return getAllowedCampuses();

    try {
      const usersRef = collection(db, 'Users');
      const userQuery = query(usersRef, where('email', '==', email));
      const snapshot = await getDocs(userQuery);

      if (snapshot.empty) return getAllowedCampuses();

      const userData = snapshot.docs[0].data();
      const campusCode = userData.campusCode || [];
      const allowedCampuses = Array.isArray(campusCode) ? campusCode.filter(Boolean) : [campusCode].filter(Boolean);

      localStorage.setItem('campusCode', JSON.stringify(allowedCampuses));
      localStorage.setItem('userEmail', email);
      localStorage.setItem('userType', userData.type || 'User');
      localStorage.setItem('userName', userData.name || email.split('@')[0]);
      localStorage.setItem('viewOnlyMaleData', String(!!userData.viewOnlyMaleData));
      localStorage.setItem('viewOnlyFemaleData', String(!!userData.viewOnlyFemaleData));

      return allowedCampuses;
    } catch (err) {
      console.error('Error refreshing user campus access:', err);
      return getAllowedCampuses();
    }
  }, [getAllowedCampuses]);

  const getCampusFilteredDocs = useCallback(async (collectionName) => {
    const allowedCampuses = getAllowedCampuses();
    const collectionRef = collection(db, collectionName);

    if (allowedCampuses.length === 0) {
      const snapshot = await getDocs(collectionRef);
      return snapshot.docs;
    }

    if (allowedCampuses.length === 1) {
      const snapshot = await getDocs(query(collectionRef, where('campusCode', '==', allowedCampuses[0])));
      return snapshot.docs;
    }

    const campusChunks = [];
    for (let i = 0; i < allowedCampuses.length; i += 30) {
      campusChunks.push(allowedCampuses.slice(i, i + 30));
    }

    const snapshots = await Promise.all(
      campusChunks.map(chunk => getDocs(query(collectionRef, where('campusCode', 'in', chunk))))
    );

    return snapshots.flatMap(snapshot => snapshot.docs);
  }, [getAllowedCampuses]);

  const getAllDocs = useCallback(async (collectionName) => {
    const snapshot = await getDocs(collection(db, collectionName));
    return snapshot.docs;
  }, []);

  const fetchAllAttendance = useCallback(async () => {
    const docs = await getAllDocs('Attendance');
    return docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }, [getAllDocs]);

  // Fetch Students
  const fetchStudents = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && students.length > 0) return students;
    
    setLoadingStates(prev => ({ ...prev, students: true }));
    try {
      const docs = await getCampusFilteredDocs('Students');
      const studentList = docs.map(doc => ({
        ...doc.data(),
        id: doc.data().id || doc.id,
        docId: doc.id
      }));
      setStudents(studentList);
      return studentList;
    } catch (err) {
      console.error('Error fetching students:', err);
      setError(err.message);
      return [];
    } finally {
      setLoadingStates(prev => ({ ...prev, students: false }));
    }
  }, [students.length, getCampusFilteredDocs]);

  // Fetch Courses
  const fetchCourses = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && courses.length > 0) return courses;
    
    setLoadingStates(prev => ({ ...prev, courses: true }));
    try {
      const snapshot = await getDocs(collection(db, 'Courses'));
      const courseList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCourses(courseList);
      return courseList;
    } catch (err) {
      console.error('Error fetching courses:', err);
      setError(err.message);
      return [];
    } finally {
      setLoadingStates(prev => ({ ...prev, courses: false }));
    }
  }, [courses.length]);

  // Fetch Campuses
  const fetchCampuses = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && campuses.length > 0) return campuses;
    
    setLoadingStates(prev => ({ ...prev, campuses: true }));
    try {
      const snapshot = await getDocs(collection(db, 'Campus'));
      const campusListFull = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCampusesAllUnfiltered(campusListFull);

      let campusList = campusListFull;
      // Filter by allowed campuses if set
      const allowedCampus = getAllowedCampuses();
      if (allowedCampus.length > 0) {
        campusList = campusList.filter(c => allowedCampus.includes(c.campusCode));
      }

      setCampuses(campusList);
      return campusList;
    } catch (err) {
      console.error('Error fetching campuses:', err);
      setError(err.message);
      return [];
    } finally {
      setLoadingStates(prev => ({ ...prev, campuses: false }));
    }
  }, [campuses.length, getAllowedCampuses]);

  // Fetch Tests
  const fetchTests = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && tests.length > 0) return tests;
    
    setLoadingStates(prev => ({ ...prev, tests: true }));
    try {
      const snapshot = await getDocs(collection(db, 'Tests'));
      const testList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort by sequence number
      testList.sort((a, b) => (a.sequence || 999) - (b.sequence || 999));
      setTests(testList);
      return testList;
    } catch (err) {
      console.error('Error fetching tests:', err);
      setError(err.message);
      return [];
    } finally {
      setLoadingStates(prev => ({ ...prev, tests: false }));
    }
  }, [tests.length]);

  // Fetch Attendance
  const fetchAttendance = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && attendance.length > 0) return attendance;
    
    setLoadingStates(prev => ({ ...prev, attendance: true }));
    try {
      const docs = await getCampusFilteredDocs('Attendance');
      const attendanceList = docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAttendance(attendanceList);
      return attendanceList;
    } catch (err) {
      console.error('Error fetching attendance:', err);
      setError(err.message);
      return [];
    } finally {
      setLoadingStates(prev => ({ ...prev, attendance: false }));
    }
  }, [attendance.length, getCampusFilteredDocs]);

  // Fetch Users
  const fetchUsers = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && users.length > 0) return users;
    
    setLoadingStates(prev => ({ ...prev, users: true }));
    try {
      const snapshot = await getDocs(collection(db, 'users'));
      const userList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(userList);
      return userList;
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message);
      return [];
    } finally {
      setLoadingStates(prev => ({ ...prev, users: false }));
    }
  }, [users.length]);

  // Fetch StudentMarks (row-wise marks)
  const fetchStudentMarks = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && studentMarks.length > 0) return studentMarks;
    
    setLoadingStates(prev => ({ ...prev, studentMarks: true }));
    try {
      const docs = await getCampusFilteredDocs('StudentMarks');
      const marksList = docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setStudentMarks(marksList);
      return marksList;
    } catch (err) {
      console.error('Error fetching student marks:', err);
      setError(err.message);
      return [];
    } finally {
      setLoadingStates(prev => ({ ...prev, studentMarks: false }));
    }
  }, [studentMarks.length, getCampusFilteredDocs]);

  // Fetch Instructors (teachers directory)
  const fetchInstructors = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && instructors.length > 0) return instructors;

    setLoadingStates(prev => ({ ...prev, instructors: true }));
    try {
      const snapshot = await getDocs(collection(db, 'Instructors'));
      const list = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      list.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
      setInstructors(list);
      return list;
    } catch (err) {
      console.error('Error fetching instructors:', err);
      setError(err.message);
      return [];
    } finally {
      setLoadingStates(prev => ({ ...prev, instructors: false }));
    }
  }, [instructors.length]);

  // Load all data at once
  const loadAllData = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    
    try {
      await refreshCurrentUserAccess();
      await Promise.all([
        fetchCampuses(forceRefresh),
        fetchCourses(forceRefresh),
        fetchStudents(forceRefresh),
        fetchTests(forceRefresh),
        fetchAttendance(forceRefresh),
        fetchUsers(forceRefresh),
        fetchInstructors(forceRefresh)
      ]);
      setInitialLoadComplete(true);
    } catch (err) {
      console.error('Error loading all data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [refreshCurrentUserAccess, fetchCampuses, fetchCourses, fetchStudents, fetchTests, fetchAttendance, fetchUsers, fetchInstructors]);

  // Refresh specific collection
  const refreshCollection = useCallback(async (collectionName) => {
    switch (collectionName) {
      case 'students':
        return fetchStudents(true);
      case 'courses':
        return fetchCourses(true);
      case 'campuses':
        return fetchCampuses(true);
      case 'tests':
        return fetchTests(true);
      case 'attendance':
        return fetchAttendance(true);
      case 'users':
        return fetchUsers(true);
      case 'studentMarks':
        return fetchStudentMarks(true);
      case 'instructors':
        return fetchInstructors(true);
      default:
        console.warn(`Unknown collection: ${collectionName}`);
        return null;
    }
  }, [fetchStudents, fetchCourses, fetchCampuses, fetchTests, fetchAttendance, fetchUsers, fetchStudentMarks, fetchInstructors]);

  // Refresh all data
  const refreshAllData = useCallback(() => {
    return loadAllData(true);
  }, [loadAllData]);

  // Update local state (for optimistic updates)
  const updateLocalStudent = useCallback((studentId, updates) => {
    setStudents(prev => prev.map(s => 
      s.id === studentId ? { ...s, ...updates } : s
    ));
  }, []);

  const addLocalStudent = useCallback((newStudent) => {
    setStudents(prev => [...prev, newStudent]);
  }, []);

  const removeLocalStudent = useCallback((studentId) => {
    setStudents(prev => prev.filter(s => s.id !== studentId));
  }, []);

  const updateLocalAttendance = useCallback((attendanceId, updates) => {
    setAttendance(prev => prev.map(a => 
      a.id === attendanceId ? { ...a, ...updates } : a
    ));
  }, []);

  const addLocalAttendance = useCallback((newAttendance) => {
    setAttendance(prev => [...prev, newAttendance]);
  }, []);

  const upsertLocalStudentMark = useCallback((studentId, testId, updates) => {
    setStudentMarks(prev => {
      const id = `${studentId}_${testId}`;
      const idx = prev.findIndex(m => m.id === id);
      if (idx === -1) {
        return [...prev, { id, studentId, testId, ...updates }];
      }
      const next = [...prev];
      next[idx] = { ...next[idx], ...updates };
      return next;
    });
  }, []);

  // Helper functions for filtered data
  const getStudentsByCampus = useCallback((campusCodes) => {
    if (!campusCodes || campusCodes.length === 0) return students;
    return students.filter(s => 
      s.campusCode && campusCodes.includes(s.campusCode)
    );
  }, [students]);

  const getTestsByCampus = useCallback((campusCodes) => {
    if (!campusCodes || campusCodes.length === 0) return tests;
    return tests.filter(t => 
      t.campusCodes && campusCodes.some(code => t.campusCodes.includes(code))
    );
  }, [tests]);

  const getTestsByCourse = useCallback((courseId) => {
    if (!courseId) return tests;
    return tests.filter(t => t.courseId === courseId || t.courseId === String(courseId));
  }, [tests]);

  const getCoursesByCampus = useCallback((campusCodes) => {
    if (!campusCodes || campusCodes.length === 0) return courses;
    return courses.filter(c => 
      c.campusCodes && c.campusCodes.some(code => campusCodes.includes(code))
    );
  }, [courses]);

  const getAttendanceByCourse = useCallback((courseName) => {
    if (!courseName) return attendance;
    return attendance.filter(a => a.courseName === courseName);
  }, [attendance]);

  const getCourseById = useCallback((courseId) => {
    return courses.find(c => c.id === courseId);
  }, [courses]);

  const getStudentById = useCallback((studentId) => {
    return students.find(s => s.id === studentId);
  }, [students]);

  // Marks helpers
  const getMarksByCourseAndCampus = useCallback((campusCode, courseId) => {
    if (!studentMarks.length) return {};

    return studentMarks.reduce((acc, mark) => {
      if (campusCode && mark.campusCode !== campusCode) return acc;
      if (courseId && !(mark.courseId === courseId || mark.courseId === String(courseId))) return acc;

      if (!acc[mark.studentId]) {
        acc[mark.studentId] = {};
      }
      acc[mark.studentId][mark.testId] = mark.mark ?? 0;
      return acc;
    }, {});
  }, [studentMarks]);

  const getMarksForStudent = useCallback((studentId) => {
    return studentMarks.filter(m => m.studentId === studentId);
  }, [studentMarks]);

  // Load data on mount
  useEffect(() => {
    loadAllData();
  }, []);

  // Context value
  const value = {
    // Raw data
    students,
    courses,
    campuses,
    campusesAllUnfiltered,
    tests,
    attendance,
    users,
    studentMarks,
    instructors,

    // Loading states
    loading,
    initialLoadComplete,
    loadingStates,
    error,

    // Fetch functions (with caching)
    fetchStudents,
    fetchCourses,
    fetchCampuses,
    fetchTests,
    fetchAttendance,
    fetchAllAttendance,
    fetchUsers,
    fetchStudentMarks,
    fetchInstructors,

    // Refresh functions (force reload)
    refreshCollection,
    refreshAllData,
    loadAllData,

    // Local state updates (optimistic)
    updateLocalStudent,
    addLocalStudent,
    removeLocalStudent,
    updateLocalAttendance,
    addLocalAttendance,
    upsertLocalStudentMark,

    // Helper/Filter functions
    getStudentsByCampus,
    getTestsByCampus,
    getTestsByCourse,
    getCoursesByCampus,
    getAttendanceByCourse,
    getCourseById,
    getStudentById,
    getAllowedCampuses,
    getMarksByCourseAndCampus,
    getMarksForStudent
  };

  return (
    <FirebaseDataContext.Provider value={value}>
      {children}
    </FirebaseDataContext.Provider>
  );
};

export default FirebaseDataContext;

